<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import { laudemioTypes } from '../composables/salesPropertyFormOptions';
import SalesAICopyAlert from './SalesAICopyAlert.vue';
import SalesPropertyReferenceField from './SalesPropertyReferenceField.vue';

const props = defineProps({
  profile: { type: Object, required: true },
  errors: { type: Object, default: () => ({}) },
  catalog: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['update']);
const { t } = useI18n();
const enabled = computed(() => props.profile.laudemios?.length > 0);
const types = computed(() => laudemioTypes(t));
const families = computed(() => props.catalog.familias || []);
const churches = computed(() => props.catalog.igrejas || []);

const setEnabled = value => {
  emit(
    'update',
    'laudemios',
    value ? [{ tipo_laudemio: '', valor_laudemio: '' }] : []
  );
};

const setCount = count => {
  const current = props.profile.laudemios || [];
  const next = Array.from(
    { length: Number(count) },
    (_, index) => current[index] || { tipo_laudemio: '', valor_laudemio: '' }
  );
  emit('update', 'laudemios', next);
};

const setItem = (index, field, value) => {
  const next = (props.profile.laudemios || []).map((item, itemIndex) =>
    itemIndex === index
      ? {
          ...item,
          [field]: value,
          ...(field === 'tipo_laudemio' ? { valor_laudemio: '' } : {}),
        }
      : item
  );
  emit('update', 'laudemios', next);
};

const valueOptions = type => (type === '3' ? families.value : churches.value);
const valueLabel = type => {
  if (type === '1') return t('SALES_PROPERTY.LAUDEMIO.RIP');
  if (type === '3') return t('SALES_PROPERTY.LAUDEMIO.FAMILY');
  return t('SALES_PROPERTY.LAUDEMIO.CHURCH');
};
</script>

<template>
  <LaisCard
    density="flush"
    class="self-start !border-0 !shadow-sm"
    data-test="sales-property-laudemio-section"
  >
    <div class="grid p-8 font-roboto">
      <h2
        class="mb-2 font-lato text-[32px] font-bold leading-[39px] text-[#464F53] max-[700px]:text-[24px] max-[700px]:leading-[29px]"
      >
        {{ t('SALES_PROPERTY.LAUDEMIO.TITLE') }}
      </h2>
      <p
        class="mb-[22px] text-xl leading-6 text-[#464F53] max-[700px]:mb-[44px]"
        data-test="laudemio-description"
      >
        {{ t('SALES_PROPERTY.LAUDEMIO.DESCRIPTION') }}
      </p>
      <SalesAICopyAlert :message="t('SALES_PROPERTY.COMMON.AI_COPY_WARNING')" />
      <div
        class="mt-[36px] mb-[51px] w-full max-w-[276px]"
        data-test="laudemio-toggle"
      >
        <button
          type="button"
          role="switch"
          :aria-checked="enabled"
          class="flex h-9 w-full items-center gap-1 rounded-[10px] pr-[13px] text-left"
          :class="enabled ? 'bg-[#EDF8F7]' : 'bg-transparent'"
          @click="setEnabled(!enabled)"
        >
          <span class="relative inline-flex h-9 w-[58px] shrink-0">
            <span
              class="absolute top-[11px] left-3 h-[14px] w-[34px] rounded-full"
              :class="enabled ? 'bg-[#80DED6]' : 'bg-[#A7B7BE]'"
            />
            <span
              class="absolute top-2 left-[9px] size-5 rounded-full transition-transform"
              :class="enabled ? 'translate-x-5 bg-[#01988C]' : 'bg-[#F1F4F5]'"
            />
          </span>
          <span
            class="whitespace-nowrap text-base"
            :class="enabled ? 'text-[#01988C]' : 'text-[#464F53]'"
          >
            {{ t('SALES_PROPERTY.LAUDEMIO.ENABLED') }}
          </span>
        </button>
      </div>
      <div v-if="enabled" class="grid gap-0">
        <SalesPropertyReferenceField
          :filled="Boolean(props.profile.laudemios.length)"
          :label="t('SALES_PROPERTY.LAUDEMIO.QUANTITY')"
          indicator="select"
          required
          class="mb-8"
        >
          <select
            :value="String(props.profile.laudemios.length)"
            data-testid="laudemio-quantity"
            class="h-8 w-full appearance-none border-0 border-b border-[#009F94] bg-transparent bg-none px-0 pr-7 text-base font-bold text-[#009F94] outline-none"
            @change="setCount($event.target.value)"
          >
            <option value="" disabled>
              {{ t('SALES_PROPERTY.LAUDEMIO.QUANTITY_PLACEHOLDER') }}
            </option>
            <option v-for="count in 4" :key="count" :value="count">
              {{ count }}
            </option>
          </select>
        </SalesPropertyReferenceField>
        <div
          v-for="(item, index) in props.profile.laudemios"
          :key="index"
          data-testid="laudemio-item"
          class="flex w-full min-w-0"
        >
          <SalesPropertyReferenceField
            :filled="Boolean(item.tipo_laudemio)"
            :label="t('SALES_PROPERTY.LAUDEMIO.TYPE')"
            :error="errors.laudemios"
            indicator="select"
            required
            class="mb-8 w-[calc(50%-8px)] min-w-0 shrink-0 [&>span:first-child]:truncate"
          >
            <select
              :value="item.tipo_laudemio"
              data-testid="laudemio-type"
              class="h-8 w-full appearance-none border-0 border-b border-[#009F94] bg-transparent bg-none px-0 pr-7 text-base font-bold text-[#009F94] outline-none"
              @change="setItem(index, 'tipo_laudemio', $event.target.value)"
            >
              <option value="" disabled>
                {{ t('SALES_PROPERTY.LAUDEMIO.TYPE_PLACEHOLDER') }}
              </option>
              <option
                v-for="option in types"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </SalesPropertyReferenceField>
          <div class="ml-4 w-[calc(50%-8px)] min-w-0 shrink-0">
            <SalesPropertyReferenceField
              v-if="item.tipo_laudemio && item.tipo_laudemio !== '2'"
              :filled="Boolean(item.valor_laudemio)"
              :label="valueLabel(item.tipo_laudemio)"
              :indicator="item.tipo_laudemio === '1' ? 'success' : 'select'"
              required
              class="mb-8 [&>span:first-child]:truncate"
            >
              <input
                v-if="item.tipo_laudemio === '1'"
                :value="item.valor_laudemio"
                data-testid="laudemio-value"
                :placeholder="t('SALES_PROPERTY.LAUDEMIO.RIP_PLACEHOLDER')"
                class="h-8 w-full border-0 border-b border-[#009F94] bg-transparent px-0 text-base font-bold text-[#009F94] outline-none placeholder:text-[#A7B7BE]"
                @input="setItem(index, 'valor_laudemio', $event.target.value)"
              />
              <select
                v-else
                :value="item.valor_laudemio"
                data-testid="laudemio-value"
                class="h-8 w-full appearance-none border-0 border-b border-[#009F94] bg-transparent bg-none px-0 pr-7 text-base font-bold text-[#009F94] outline-none"
                @change="setItem(index, 'valor_laudemio', $event.target.value)"
              >
                <option value="" disabled>
                  {{ t('SALES_PROPERTY.LAUDEMIO.CATALOG_PLACEHOLDER') }}
                </option>
                <option
                  v-for="option in valueOptions(item.tipo_laudemio)"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </SalesPropertyReferenceField>
          </div>
        </div>
      </div>
      <slot name="footer" />
    </div>
  </LaisCard>
</template>
